import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Github,
  ExternalLink,
  Calendar,
  Code2,
  Users,
  Star,
  ArrowLeft,
  Image as ImageIcon,
  Video,
  Clock,
  CheckCircle2,
  Sparkles,
  Layers,
  ChevronLeft,
  ChevronRight,
  Monitor,
} from 'lucide-react';
import { projectsData } from '../data/projectsData';
import { ProjectArchitecture, ProjectCodeMap } from './ProjectArchitecture';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import ProjectTopBar from './ProjectTopBar';
import CaseStudyPage from './CaseStudyPage';
import { Reveal, RevealGroup, RevealItem } from './ui/Reveal';
import { usePageMeta } from '../lib/usePageMeta';

export default function ProjectDetailsPage({ project: propProject, onBack }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedImage, setSelectedImage] = useState(0);
  const [project, setProject] = useState(propProject || null);
  const [loading, setLoading] = useState(!propProject);
  const { id, slug } = useParams();

  const metaSlug = project?.title ? project.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') : '';
  const metaDescription = project?.description ? project.description.replace(/\s+/g, ' ').slice(0, 158).replace(/\s\S*$/, '') + (project.description.length > 158 ? '…' : '') : undefined;
  usePageMeta({
    title: project?.title ? `${project.title}${project.subtitle ? `: ${project.subtitle}` : ''} | Rakshan Shetty` : undefined,
    description: metaDescription,
    path: project ? `/project/${project.id}/${metaSlug}` : undefined,
  });
  const navigate = useNavigate();

  const isValidLiveUrl = (url) => {
    if (!url || url === '#') return false;
    const placeholderUrls = [
      'https://your-ecommerce-demo.com',
      'https://nexttrip-demo.com',
      'https://doctor-appointment-demo.com',
      'https://your-sgpa-calculator.com',
      'https://music-genre-classifier-demo.com',
      'https://blind-voting-system-demo.com',
      'https://brain-stroke-prediction-demo.com',
      'https://automated-email-responder-demo.com',
      'https://smart-door-lock-demo.com'
    ];
    return !placeholderUrls.includes(url);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (propProject) {
      setProject(propProject);
      setLoading(false);
      return;
    }

    const loadProjectData = async () => {
      try {
        const findProjectById = (targetId) => {
          for (const category of Object.values(projectsData)) {
            const foundProject = category.projects.find(p => p.id === parseInt(targetId));
            if (foundProject) return foundProject;
          }
          return null;
        };

        const foundProject = findProjectById(id);
        if (foundProject) {
          setProject(foundProject);
          sessionStorage.setItem('selectedProject', JSON.stringify(foundProject));
          setLoading(false);
          return;
        }

        const storedProject = sessionStorage.getItem('selectedProject');
        if (storedProject) {
          const parsedProject = JSON.parse(storedProject);
          if (String(parsedProject?.id) === String(id)) {
            setProject(parsedProject);
          }
        }
      } catch (error) {
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
  }, [id, slug, propProject]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById('projects');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const hasTechnicalContent = Boolean(
    project?.challenges ||
    project?.solution ||
    project?.architecture ||
    project?.codeMap
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Star, count: null },
    {
      id: 'gallery',
      label: 'Screenshots',
      icon: ImageIcon,
      count: project?.images?.length || 0
    },
    ...(project?.videoUrl
      ? [{ id: 'video', label: 'Demo', icon: Video, count: null }]
      : []),
    ...(hasTechnicalContent
      ? [{ id: 'technical', label: 'Architecture', icon: Layers, count: null }]
      : [])
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-canvas)] text-[var(--fg-primary)] flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 rounded-full border-2 border-[var(--border-ring)] border-t-[var(--accent)] animate-spin mx-auto" />
          <p className="t-mono text-xs text-[var(--fg-secondary)]">Loading project specification...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[var(--bg-canvas)] text-[var(--fg-primary)] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-5 p-8 rounded-2xl bg-[var(--bg-elevated)] shadow-[var(--shadow-sm)]">
          <div className="w-12 h-12 rounded-full bg-[var(--bg-recessed)] shadow-[var(--border-ring)] flex items-center justify-center mx-auto text-[var(--fg-muted)]">
            <Code2 size={24} />
          </div>
          <div>
            <h1 className="t-h3 text-[var(--fg-primary)] mb-2">Project Not Found</h1>
            <p className="text-sm text-[var(--fg-secondary)] leading-relaxed">
              The project you are looking for does not exist or may have been moved.
            </p>
          </div>
          <Button asChild variant="primary" size="md" className="w-full">
            <a href="/#projects">
              <ArrowLeft size={16} className="mr-2" />
              Return to Projects
            </a>
          </Button>
        </div>
      </div>
    );
  }

  if (project.caseStudy) {
    return <CaseStudyPage project={project} onBack={handleBack} />;
  }

  const isLive = isValidLiveUrl(project?.liveUrl);

  return (
    <div className="min-h-screen bg-[var(--bg-canvas)] text-[var(--fg-primary)] flex flex-col selection:bg-[var(--accent)]/20">
      <ProjectTopBar project={project} onBack={handleBack} />

      {/* Hero Headline Section with Subtle Watermark Number */}
      <section className="relative overflow-hidden border-b border-[var(--bg-recessed)] py-14 md:py-20 px-6 bg-[var(--bg-canvas)]">
        {/* Oversized background watermark index number with parallax/opacity */}
        <div className="absolute right-2 sm:right-10 top-1/2 -translate-y-1/2 text-[16rem] sm:text-[22rem] md:text-[28rem] font-semibold text-[var(--fg-primary)] opacity-[0.03] select-none pointer-events-none leading-none tracking-tighter">
          {String(project.id).padStart(2, '0')}
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto">
          <Reveal>
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <Badge className="t-mono text-[11px] py-0.5">
                PROJECT #{String(project.id).padStart(2, '0')}
              </Badge>

              <Badge className="t-mono text-[11px] py-0.5">
                {project.status || 'Active'}
              </Badge>

              {project.category && (
                <Badge className="t-mono text-[11px] py-0.5">
                  {project.category.toUpperCase()}
                </Badge>
              )}

              {project.featured && (
                <Badge className="flex items-center gap-1 text-[11px] py-0.5">
                  <Star size={11} className="fill-[var(--fg-primary)] text-[var(--fg-primary)]" />
                  Featured Build
                </Badge>
              )}

              <span className="t-mono text-xs text-[var(--fg-muted)] ml-auto hidden sm:inline">
                {project.duration || project.date || 'Production'}
              </span>
            </div>

            <h1 className="t-display text-4xl sm:text-5xl md:text-6xl text-[var(--fg-primary)] max-w-4xl tracking-tight leading-[1.08] mb-5">
              {project.title}
            </h1>

            <p className="text-base sm:text-lg text-[var(--fg-secondary)] max-w-3xl leading-relaxed mb-8 font-normal">
              {project.subtitle || project.description}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              {isLive ? (
                <Button asChild variant="primary" size="md">
                  <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={15} className="mr-2" />
                    Visit Live Application
                  </a>
                </Button>
              ) : (
                <Button variant="ghost" size="md" className="opacity-50 cursor-not-allowed" disabled>
                  <ExternalLink size={15} className="mr-2" />
                  Live Demo Unavailable
                </Button>
              )}

              {project.githubUrl && (
                <Button asChild variant="secondary" size="md">
                  <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                    <Github size={15} className="mr-2" />
                    Source Code
                  </a>
                </Button>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Tab Navigation Strip - Floating Segmented Pill */}
      <div className="sticky top-14 z-40 bg-[var(--bg-canvas)]/95 border-b border-[var(--bg-recessed)] px-6 py-2.5 transition-colors">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between gap-4">
          <nav className="inline-flex items-center p-1 rounded-full border border-[var(--bg-recessed)] bg-[var(--bg-canvas)] shadow-[var(--shadow-sm)]">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                    isActive
                      ? 'text-[var(--fg-primary)] font-semibold'
                      : 'text-[var(--fg-secondary)] hover:text-[var(--fg-primary)]'
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="project-detail-tab-pill"
                      className="absolute inset-0 rounded-full bg-[var(--bg-elevated)] border border-[var(--bg-recessed)] shadow-[var(--shadow-sm)] z-0"
                      transition={{ type: 'spring', stiffness: 450, damping: 35 }}
                    />
                  )}
                  <Icon size={14} className={`relative z-10 ${isActive ? 'text-[var(--accent)]' : 'text-[var(--fg-muted)]'}`} />
                  <span className="relative z-10">{tab.label}</span>
                  {typeof tab.count === 'number' && tab.count > 0 && (
                    <span className="relative z-10 ml-0.5 t-mono text-[10px] px-1.5 py-0.2 bg-[var(--bg-recessed)] text-[var(--fg-secondary)] rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="hidden sm:flex items-center gap-2 text-xs t-mono text-[var(--fg-muted)]">
            <span>{project.category || 'Engineering'}</span>
            <span>/</span>
            <span>{project.duration || 'Production'}</span>
          </div>
        </div>
      </div>

      {/* Main Tab Body */}
      <main className="flex-1 max-w-[1200px] w-full mx-auto px-6 py-10 md:py-14">
        {/* TAB: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Left 2 Cols: Detailed Narrative & Features Grid */}
            <div className="lg:col-span-2 space-y-8">
              <Card className="p-6 sm:p-8">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--bg-recessed)]">
                  <div className="flex items-center gap-2">
                    <Monitor size={16} className="text-[var(--accent)]" />
                    <h2 className="t-h5 text-[var(--fg-primary)]">Architecture & Summary</h2>
                  </div>
                  <span className="t-mono text-xs text-[var(--fg-muted)]">Core Narrative</span>
                </div>
                <div className="space-y-4 text-sm sm:text-base leading-relaxed text-[var(--fg-secondary)]">
                  <p>{project.longDescription || project.description}</p>
                </div>
              </Card>

              {project.features && project.features.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles size={16} className="text-[var(--accent)]" />
                      <h3 className="t-h5 text-[var(--fg-primary)]">Key Features & Capabilities</h3>
                    </div>
                    <span className="t-mono text-xs text-[var(--fg-muted)]">
                      {project.features.length} capabilities
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {project.features.map((feature, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-xl border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] hover:border-[var(--border-ring)] transition-all flex items-start gap-3 shadow-[var(--shadow-sm)] group"
                      >
                        <span className="t-mono text-[10px] text-[var(--fg-muted)] group-hover:text-[var(--accent)] shrink-0 mt-0.5 font-medium">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <p className="text-xs sm:text-sm text-[var(--fg-secondary)] group-hover:text-[var(--fg-primary)] leading-relaxed transition-colors">
                          {feature}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Jump Callout to Technical Deep Dive if available */}
              {hasTechnicalContent && (
                <div className="p-6 rounded-xl border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-[var(--shadow-sm)]">
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold text-[var(--fg-primary)]">
                      Detailed Technical Breakdown Available
                    </h4>
                    <p className="text-xs text-[var(--fg-secondary)]">
                      Explore the system architecture layers, engineering challenges, and interactive code map.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setActiveTab('technical')}
                    className="shrink-0"
                  >
                    <span>View Architecture</span>
                    <ChevronRight size={14} className="ml-1" />
                  </Button>
                </div>
              )}
            </div>

            {/* Right 1 Col: Metadata Sidebar */}
            <div className="space-y-6">
              {/* Meta Specifications */}
              <Card className="p-6">
                <h3 className="t-h6 text-[var(--fg-primary)] mb-4 pb-3 border-b border-[var(--bg-recessed)]">
                  Specification
                </h3>

                <dl className="space-y-3.5 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <dt className="flex items-center gap-2 text-[var(--fg-muted)]">
                      <Calendar size={14} />
                      <span>Timeline</span>
                    </dt>
                    <dd className="t-mono font-medium text-[var(--fg-primary)]">{project.date || '2024 - 2025'}</dd>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <dt className="flex items-center gap-2 text-[var(--fg-muted)]">
                      <Clock size={14} />
                      <span>Duration</span>
                    </dt>
                    <dd className="t-mono font-medium text-[var(--fg-primary)]">{project.duration || '2 months'}</dd>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <dt className="flex items-center gap-2 text-[var(--fg-muted)]">
                      <Users size={14} />
                      <span>Team Capacity</span>
                    </dt>
                    <dd className="t-mono font-medium text-[var(--fg-primary)]">{project.team || 'Solo Developer'}</dd>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <dt className="flex items-center gap-2 text-[var(--fg-muted)]">
                      <Layers size={14} />
                      <span>Status</span>
                    </dt>
                    <dd className="t-mono font-medium text-[var(--fg-primary)]">{project.status || 'Active'}</dd>
                  </div>
                </dl>
              </Card>

              {/* Technologies & Tooling */}
              <Card className="p-6">
                <h3 className="t-h6 text-[var(--fg-primary)] mb-3 pb-3 border-b border-[var(--bg-recessed)]">
                  Technologies
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {(project.technologies || project.tech || []).map((t, index) => (
                    <Badge key={index} className="py-1 px-2.5 text-xs t-mono">
                      {t}
                    </Badge>
                  ))}
                </div>
              </Card>

              {/* Links Card */}
              <Card className="p-6">
                <h3 className="t-h6 text-[var(--fg-primary)] mb-3 pb-3 border-b border-[var(--bg-recessed)]">
                  Repository & Deploy
                </h3>
                <div className="space-y-2">
                  {isLive && (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-2.5 rounded-lg bg-[var(--bg-recessed)] hover:bg-[var(--bg-hover)] text-xs font-medium text-[var(--fg-primary)] transition-colors group cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <ExternalLink size={13} className="text-[var(--accent)]" />
                        Live Deployment
                      </span>
                      <ChevronRight size={12} className="text-[var(--fg-muted)] group-hover:text-[var(--fg-primary)] transition-colors" />
                    </a>
                  )}

                  {project.githubUrl && (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-2.5 rounded-lg bg-[var(--bg-recessed)] hover:bg-[var(--bg-hover)] text-xs font-medium text-[var(--fg-primary)] transition-colors group cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <Github size={13} />
                        GitHub Repository
                      </span>
                      <ChevronRight size={12} className="text-[var(--fg-muted)] group-hover:text-[var(--fg-primary)] transition-colors" />
                    </a>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* TAB: GALLERY */}
        {activeTab === 'gallery' && (
          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="t-h4 text-[var(--fg-primary)]">Interface & Screenshots</h2>
                <p className="text-sm text-[var(--fg-secondary)] mt-1">
                  High-resolution views of the application interface and dashboards.
                </p>
              </div>
              <span className="t-mono text-xs text-[var(--fg-muted)]">
                {String(selectedImage + 1).padStart(2, '0')} / {String(project.images?.length || 0).padStart(2, '0')}
              </span>
            </div>

            {project.images && project.images.length > 0 ? (
              <div className="space-y-4">
                {/* Browser Mockup Frame */}
                <div className="rounded-xl border border-[var(--bg-recessed)] bg-[var(--bg-canvas)] p-3 shadow-[var(--shadow-sm)]">
                  {/* Window Chrome Header */}
                  <div className="flex items-center justify-between pb-3 mb-3 border-b border-[var(--bg-recessed)]">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--fg-muted)] opacity-30" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--fg-muted)] opacity-30" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--fg-muted)] opacity-30" />
                    </div>
                    <div className="flex-1 max-w-[260px] mx-auto px-2.5 py-0.5 rounded bg-[var(--bg-recessed)] text-center">
                      <span className="t-mono text-[10px] text-[var(--fg-secondary)] truncate block">
                        https://{(project.title || 'app').toLowerCase().replace(/[^a-z0-9]/g, '-')}.app
                      </span>
                    </div>
                    <span className="t-mono text-[10px] text-[var(--fg-muted)]">
                      {selectedImage + 1} of {project.images.length}
                    </span>
                  </div>

                  {/* Mockup Canvas */}
                  <div className="w-full aspect-[16/10] bg-[var(--bg-recessed)] rounded-lg overflow-hidden flex items-center justify-center relative">
                    {project.images[selectedImage] && !project.images[selectedImage].includes('Image_not_available') ? (
                      <img
                        src={project.images[selectedImage]}
                        alt={`${project.title} screenshot ${selectedImage + 1}`}
                        className="object-contain w-full h-full max-h-[560px]"
                      />
                    ) : (
                      <div className="p-8 text-center space-y-3">
                        <div className="w-14 h-14 rounded-2xl bg-[var(--bg-elevated)] border border-[var(--bg-recessed)] shadow-[var(--shadow-sm)] flex items-center justify-center mx-auto text-[var(--fg-secondary)]">
                          <Code2 size={24} />
                        </div>
                        <div>
                          <h4 className="t-h5 text-[var(--fg-primary)] mb-1">{project.title}</h4>
                          <p className="text-xs text-[var(--fg-secondary)] max-w-sm mx-auto">
                            Interface preview and operational workspace specification
                          </p>
                        </div>
                        <div className="flex items-center justify-center gap-2 pt-1">
                          <Badge className="t-mono text-[10px]">{project.category || 'Production'}</Badge>
                          <Badge className="t-mono text-[10px]">{project.tech?.[0] || 'Web'}</Badge>
                        </div>
                      </div>
                    )}

                    {project.images.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() => setSelectedImage((prev) => (prev > 0 ? prev - 1 : project.images.length - 1))}
                          aria-label="Previous screenshot"
                          className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[var(--bg-elevated)] text-[var(--fg-primary)] shadow-[var(--shadow-md)] border border-[var(--bg-recessed)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                        >
                          <ChevronLeft size={18} />
                        </button>

                        <button
                          type="button"
                          onClick={() => setSelectedImage((prev) => (prev < project.images.length - 1 ? prev + 1 : 0))}
                          aria-label="Next screenshot"
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[var(--bg-elevated)] text-[var(--fg-primary)] shadow-[var(--shadow-md)] border border-[var(--bg-recessed)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Thumbnails Row */}
                {project.images.length > 1 && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 gap-2.5 pt-2">
                    {project.images.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedImage(idx)}
                        className={`aspect-video rounded-lg p-1 transition-all cursor-pointer bg-[var(--bg-elevated)] ${
                          selectedImage === idx
                            ? 'ring-2 ring-[var(--accent)] shadow-[var(--shadow-sm)]'
                            : 'border border-[var(--bg-recessed)] hover:border-[var(--fg-muted)] opacity-70 hover:opacity-100'
                        }`}
                      >
                        <div className="w-full h-full rounded overflow-hidden bg-[var(--bg-recessed)] flex items-center justify-center">
                          {img && !img.includes('Image_not_available') ? (
                            <img
                              src={img}
                              alt={`Thumbnail ${idx + 1}`}
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <span className="t-mono text-[10px] text-[var(--fg-muted)]">{idx + 1}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Card className="p-12 text-center text-[var(--fg-secondary)]">
                <ImageIcon size={32} className="mx-auto text-[var(--fg-muted)] mb-3" />
                <p className="text-sm">No screenshots have been attached for this project yet.</p>
              </Card>
            )}
          </div>
        )}

        {/* TAB: VIDEO DEMO */}
        {activeTab === 'video' && (
          <div className="space-y-6 max-w-4xl mx-auto">
            <div>
              <h2 className="t-h4 text-[var(--fg-primary)]">Interactive Demonstration</h2>
              <p className="text-sm text-[var(--fg-secondary)] mt-1">
                Video walkthrough demonstrating the workflow and key user journeys.
              </p>
            </div>

            {project.videoUrl ? (
              <div className="rounded-xl border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] p-3 shadow-[var(--shadow-sm)]">
                <div className="w-full aspect-video bg-[var(--bg-canvas)] rounded-lg overflow-hidden">
                  <iframe
                    src={project.videoUrl}
                    title={`${project.title} Video Walkthrough`}
                    className="w-full h-full"
                    allowFullScreen
                  />
                </div>
              </div>
            ) : (
              <Card className="p-12 text-center text-[var(--fg-secondary)]">
                <Video size={32} className="mx-auto text-[var(--fg-muted)] mb-3" />
                <p className="text-sm">No demo video is currently available for this project.</p>
              </Card>
            )}
          </div>
        )}

        {/* TAB: TECHNICAL / ARCHITECTURE */}
        {activeTab === 'technical' && (
          <div className="space-y-8 max-w-4xl mx-auto">
            <div>
              <h2 className="t-h4 text-[var(--fg-primary)]">Engineering Deep Dive</h2>
              <p className="text-sm text-[var(--fg-secondary)] mt-1">
                System architecture, technical challenges overcome, and implementation decisions.
              </p>
            </div>

            {/* Challenges and Solutions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {project.challenges && (
                <Card className="p-6 sm:p-7">
                  <div className="mb-4 pb-3 border-b border-[var(--bg-recessed)] flex items-center justify-between">
                    <h3 className="t-h5 text-[var(--fg-primary)]">Engineering Challenges</h3>
                    <span className="t-mono text-[10px] text-[var(--fg-muted)] uppercase tracking-wider">Problem Space</span>
                  </div>
                  <p className="text-sm text-[var(--fg-secondary)] leading-relaxed">
                    {project.challenges}
                  </p>
                </Card>
              )}

              {project.solution && (
                <Card className="p-6 sm:p-7 border-[var(--border-ring)] bg-[var(--bg-elevated)] shadow-[var(--shadow-sm)]">
                  <div className="mb-4 pb-3 border-b border-[var(--bg-recessed)] flex items-center justify-between">
                    <h3 className="t-h5 text-[var(--fg-primary)]">Architectural Solution</h3>
                    <span className="t-mono text-[10px] text-[var(--accent)] uppercase tracking-wider">Resolution</span>
                  </div>
                  <p className="text-sm text-[var(--fg-secondary)] leading-relaxed">
                    {project.solution}
                  </p>
                </Card>
              )}
            </div>

            {/* Interactive Architecture diagram if data exists */}
            {project.architecture && (
              <div className="space-y-4 pt-4">
                <div className="flex items-center justify-between">
                  <h3 className="t-h5 text-[var(--fg-primary)]">System Architecture Layers</h3>
                  <span className="t-mono text-xs text-[var(--fg-muted)]">Data flow & boundaries</span>
                </div>
                <ProjectArchitecture
                  architecture={project.architecture}
                  repoBaseUrl={project.codeMap?.baseUrl}
                />
              </div>
            )}

            {/* Code Map File Reference */}
            {project.codeMap && (
              <div className="pt-4">
                <ProjectCodeMap codeMap={project.codeMap} />
              </div>
            )}
          </div>
        )}
      </main>

      {/* Subtle Footer */}
      <footer className="border-t border-[var(--bg-recessed)] py-8 px-6 bg-[var(--bg-canvas)] text-xs text-[var(--fg-secondary)]">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>Rakshan Shetty / Portfolio Project Specification</span>
          <button
            type="button"
            onClick={handleBack}
            className="text-[var(--fg-primary)] hover:underline cursor-pointer"
          >
            ← Return to all projects
          </button>
        </div>
      </footer>
    </div>
  );
}